import React from 'react';
import { renderToString } from 'react-dom/server';

import lineChartConfig from '../../../../components/Chart/LineChart';

import { numberToHumanChart } from '../../../../utils/formatter';
import { format } from '../../../../utils/date';

const decorateYValue = (value, postfix = '%') => value && `${numberToHumanChart(value)}${postfix}`;

const crossAppChartConfig = ({
  floor,
  ceiling,
  xAxisType = 'datetime',
  xAxisLabelStep,
  chartStyleOverride,
  formatXValue,
  formatYValue = decorateYValue,
  formatTooltipHeader,
  formatTooltipPointLabel,
  formatTooltipPointValue,
}) => {
  const animation = chartStyleOverride?.animation ?? {
    duration: 300,
  };

  return lineChartConfig({
    chartOptions: {
      type: 'spline',
      spacing: [10, 6, 0, 2],
      height: chartStyleOverride?.height || 230,
      animation,
    },
    yAxis: {
      title: null,
      floor,
      ceiling,
      minPadding: 0.01,
      maxPadding: 0.01,
      startOnTick: true,
      tickAmount: 5,
      gridLineColor: '#EDF0F5',
      labels: {
        reserveSpace: true,
        style: {
          color: '#848EA1',
          fontSize: 12,
        },
        formatter() {
          return formatYValue(this.value);
        },
      },
    },
    xAxisOptions: {
      crosshair: false,
      lineWidth: 0,
      tickLength: 0,
      type: xAxisType,
    },
    xAxisLabels: {
      formatter() {
        return formatXValue(this);
      },
      style: {
        color: '#848EA1',
        fontSize: 12,
      },
      y: 30,
      showFirstLabel: true,
      ...(xAxisLabelStep ? { step: xAxisLabelStep } : {}),
    },
    tooltipOptions: {
      backgroundColor: 'transparent',
      borderWidth: 0,
      borderRadius: 10,
      padding: 0,
      useHTML: true,
      outside: true,
      formatter() {
        let rows;

        const template = (point, index) => (
          <tr key={index}>
            <td className="p-l-0 p-b-0 pr-md">
              <div className="text-black text-14 l-h-md d-flex" style={{ maxWidth: '100%' }}>
                <span
                  className="tooltip-bubble-marker mt-xxs"
                  style={{
                    backgroundColor: point.color,
                    borderColor: point.color,
                  }}
                />
                <span>{formatTooltipPointLabel(point)}</span>
              </div>
            </td>
            <td className="text-right text-black text-14 l-h-md p-l-0 p-b-0 v-a-t text-nowrap">
              {formatTooltipPointValue(point)}
            </td>
          </tr>
        );

        if (Array.isArray(this.points)) {
          rows = this.points.map((point, index) => template(point, index));
        } else return false;

        const tooltipHeader = formatTooltipHeader(this.x);

        return renderToString(
          <div
            style={{
              minWidth: '189px',
              borderRadius: '8px',
              border: '1px solid #E1E5EB',
              padding: '16px',
              backgroundColor: '#fff',
              boxShadow: '0px 0px 24px 0px rgba(0, 0, 0, 0.08)',
            }}
          >
            {tooltipHeader && (
              <div
                style={{
                  fontSize: '12px',
                  fontStyle: 'normal',
                  fontWeight: 400,
                  lineHeight: '150%',
                  color: '#848EA1',
                  marginBottom: '8px',
                }}
              >
                {tooltipHeader}
              </div>
            )}
            <table className="table">{rows}</table>
          </div>,
        );
      },
    },
    plotOptionsSeries: {
      animation,
    },
  });
};

export const userOverlapChartConfig = ({
  floor,
  ceiling,
  chartStyleOverride,
}) => crossAppChartConfig({
  floor,
  ceiling,
  chartStyleOverride,
  formatXValue: ({ value }) => format(value, 'MM/DD'),
  formatYValue: decorateYValue,
  formatTooltipHeader: () => null,
  formatTooltipPointLabel: point => point.point.date,
  formatTooltipPointValue: point => decorateYValue(point.y),
});

export const timeSpentChartConfig = ({
  floor,
  ceiling,
  chartStyleOverride,
}) => crossAppChartConfig({
  floor,
  ceiling,
  chartStyleOverride,
  formatXValue: ({ value }) => format(value, 'MM/DD'),
  formatYValue: value => decorateYValue(value, ''),
  formatTooltipHeader: x => format(x),
  formatTooltipPointLabel: point => point.series.name,
  formatTooltipPointValue: point => decorateYValue(point.y, ' minutes'),
});
